<template>
    <div class="new-invoiced-order-form-page-3">
        <div class="d-flex align-items-center mb-4">
            <div class="flex-fill">
                <div class="alert alert-info text-center mb-0">
                    La commande n'est pas encore enregistrée, n'oubliez pas de valider.
                </div>
            </div>
            <div class="ml-3">
                <slot name="submitButton" :on-click-next="onClickNext"></slot>
            </div>
        </div>

        <div class="form-group row">
            <label for="customer" class="col-md-3 col-form-label">{{ _.capitalize(customerLabel) }}</label>
            <div class="col-md-9">
                <input type="text" class="form-control-plaintext" id="customer" :value="customer.name" readonly>
            </div>
        </div>
        <div class="form-group row">
            <label for="delivery_date" class="col-md-3 col-form-label">Date de {{ isRecurrent ? 'la première ' : ''}}livraison</label>
            <div class="col-md-9">
                <input type="text" class="form-control-plaintext" id="delivery_date" :value="formattedDeliveryDate" readonly>
            </div>
            <div v-if="isRecurrent" class="col-md-4 offset-md-3">
                <div class="alert alert-info">
                    Il s'agit de la date de la première livraison.<br>
                    Cette commande sera également programmée automatiquement pour une livraison tous les <span class="font-weight-bold text-underline">{{ deliveryDateDayName }}</span> suivants.<br>
                </div>
            </div>
        </div>
        <div class="form-group row">
            <label for="end_preparation_date" class="col-md-3 col-form-label">Date de fin de préparation</label>
            <div class="col-md-9">
                <input type="text" class="form-control-plaintext" id="end_preparation_date" :value="formattedEndPreparationDate" readonly>
            </div>
        </div>
        <div v-if="!order && !isRecurrent" class="form-group row">
            <label for="comment" class="col-md-3 col-form-label">Commentaire</label>
            <div class="col-md-9">
                <textarea class="form-control" id="comment" v-model="comment" ref="commentTextarea" @keydown="avoidLineBreaks"></textarea>
                <div class="form-check border border-danger bg-light py-2 pl-5 mt-2">
                    <input type="checkbox" id="comment-public-cb" class="form-check-input" v-model="commentPublic">
                    <label for="comment-public-cb" class="form-check-label">
                        <i class="fad fa-fw fa-eye"></i> Cocher cette case pour rendre ce commentaire visible par le client (sera affiché sur le BL/Récap)
                    </label>
                </div>
                <div class="form-check border bg-light mt-1 py-2 pl-5">
                    <input class="form-check-input" type="checkbox" id="comment-production-slip-cb" v-model="commentProductionSlip">
                    <label class="form-check-label" for="comment-production-slip-cb">
                        <i class="fad fa-fw fa-clipboard-list-check"></i> Cocher cette case pour afficher ce commentaire sur les bons de fabrication
                    </label>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <div class="h4">
                    Produits sélectionnés
                </div>

                <table class="table table-sm table-bordered">
                    <template v-for="mainCategory in orderedProductsByMainCategory">
                        <tr>
                            <th>{{ mainCategory.category.name }}</th>
                            <th>Quantité</th>
                        </tr>
                        <template v-for="category in mainCategory['productsBySubCategory']">
                            <order-product-line
                                v-for="product in category['products']"
                                :key="product.sku"
                                :sku="product.sku"
                                :label="product.name"
                                :warn-reasons="product.warn_reasons"
                                v-model="product.nb_ordered"
                                :readonly="true"
                                :highlight="false"
                            />
                            <tr>
                                <th colspan="2"></th>
                            </tr>
                        </template>
                    </template>
                </table>
            </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <slot name="alerts"></slot>

                <slot name="errors"></slot>
            </div>
        </div>
        <div class="row">
            <div class="col-md-12">
                <hr>

                <button type="button" class="btn btn-outline-dark float-left" @click="onClickBack" :disabled="loading || sending">
                    <i class="fas fa-chevron-left"></i> Retourner au panier
                </button>

                <slot name="submitButton" :on-click-next="onClickNext"></slot>
            </div>
        </div>
    </div>
</template>

<script>
    import {momentDay, momentDayName} from "../../moment-with-helpers";

    import OrderProductLine from "../OrderProductLine";

    import {orderedProductsByMainCategory} from "../../orders-utils";
    import {autoSizeTextarea, avoidLineBreaks} from "../../textarea-autoresize";

    export default {
        components: {
            'order-product-line': OrderProductLine,
        },

        props: {
            categoriesMainCategory: {
                type: Object,
                required: true,
            },
            categoriesById: {
                type: Object,
                required: true,
            },
            order: {
                type: Object,
                required: false,
            },
            customer: {
                type: Object,
                required: true,
            },
            customerLabel: {
                type: String,
                required: true,
            },
            deliveryDate: {
                type: Object,
                required: true,
            },
            endPreparationDate: {
                type: Object,
                required: true,
            },
            isRecurrent: {
                type: Boolean,
                required: false,
                default: false,
            },
            orderedProducts: {
                type: Object,
                required: true,
            },
            prefillComment: {
                type: String,
                required: false,
            },
            precheckCommentPublic: {
                type: Boolean,
                required: false,
            },
            precheckCommentProductionSlip: {
                type: Boolean,
                required: false,
            },
            loading: {
                type: Boolean,
                required: true,
            },
            sending: {
                type: Boolean,
                required: true,
            },
        },

        data() {
            return {
                comment: this.prefillComment || "",
                commentPublic: this.precheckCommentPublic || false,
                commentProductionSlip: this.precheckCommentProductionSlip || false,
            };
        },

        mounted() {
            if (this.$refs.commentTextarea) {
                autoSizeTextarea(this.$refs.commentTextarea);
            }
            this.$emit('mounted');
        },

        methods: {
            onClickBack() {
                this.$emit('back', {
                    comment: this.comment,
                    commentPublic: this.commentPublic,
                    commentProductionSlip: this.commentProductionSlip,
                });
            },

            onClickNext() {
                if (this.commentPublic && !confirm("Le client pourra voir ce message. Confirmez-vous l'ajout du commentaire ?")) {
                    return;
                }

                this.$emit('submit', {
                    comment: this.comment,
                    commentPublic: this.commentPublic,
                    commentProductionSlip: this.commentProductionSlip,
                });
            },

            avoidLineBreaks,
        },

        watch: {
            comment(newValue, oldValue) {
                this.comment = newValue.replaceAll(/[\n]+/g, ' ');
                this.$nextTick(() => {
                    if (this.$refs.commentTextarea) {
                        autoSizeTextarea(this.$refs.commentTextarea);
                    }
                })
            }
        },

        computed: {
            deliveryDateDayName() {
                return momentDayName(this.deliveryDate.date);
            },

            formattedDeliveryDate() {
                return momentDay(this.deliveryDate.date).format('dddd Do MMMM YYYY');
            },

            formattedEndPreparationDate() {
                return momentDay(this.endPreparationDate.date).format('dddd Do MMMM YYYY');
            },

            orderedProductsByMainCategory,
        }
    }
</script>
